import { createApiSlice } from '@/shared/features/query/config/query.build';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import { executeQuery } from '@/shared/features/query/utils/queryUtils';
import type { AppDispatch } from '@/app/store/store';
import type { PaymentRepository } from '../../domain/interfaces/PaymentRepository';
import type {
  PayViaLinkDetailsModel,
  PaymentContextFailureModel,
  PaymentContextResultParams,
  PaymentContextSuccessModel,
  PaymentContextVerifyModel,
  PaymentRequestModel,
} from '../../domain/models/payment.types';
import type {
  PaymentContextFailureDTO,
  PaymentContextSuccessDTO,
  PaymentContextVerifyDTO,
  PaymentMaybePayloadDTO,
  PaymentRequestResponseDTO,
  PayViaLinkDetailsDTO,
} from '../dto/payment.dto';
import {
  adaptPaymentContextFailureDtoToModel,
  adaptPaymentContextSuccessDtoToModel,
  adaptPaymentContextVerifyDtoToModel,
  adaptPaymentRequestDtoToModel,
  adaptPayViaLinkDetailsResponseDtoToModel,
} from '../mappers/payment.mapper';

type QueryHookResult<TData> = {
  data?: TData;
  isLoading?: boolean;
  error?: boolean;
  [key: string]: unknown;
};

type QueryHook<TArgs, TData> = (
  arg: TArgs,
  options?: Record<string, unknown>
) => QueryHookResult<TData>;

type EndpointInitiate<TArgs> = (
  args: TArgs,
  options?: { forceRefetch?: boolean }
) => unknown;

const paymentApiSlice = createApiSlice({
  clientId: 'paymentApi',
  tagTypes: ['Payment'],
  endpoints: {
    getPayViaLinkDetails: {
      url: (queryString: unknown) => {
        const value = String(queryString || '');
        return `/payment-context/get-details${value ? `?${value}` : ''}`;
      },
      method: 'GET',
      transformResponse: (rawResponse: unknown): PayViaLinkDetailsModel =>
        adaptPayViaLinkDetailsResponseDtoToModel(
          rawResponse as PaymentMaybePayloadDTO<PayViaLinkDetailsDTO>
        ),
      keepUnusedDataFor: 60,
      providesTags: ['Payment'],
    },
    createPaymentRequest: {
      url: (txnId: unknown) => `/paymentrequest/v2/${encodeURIComponent(String(txnId || ''))}`,
      method: 'GET',
      transformResponse: (rawResponse: unknown): PaymentRequestModel =>
        adaptPaymentRequestDtoToModel(rawResponse as PaymentRequestResponseDTO),
      keepUnusedDataFor: 0,
    },
    verifyPaymentContext: {
      url: '/payment-context/verify-payment',
      method: 'GET',
      params: (txnId: unknown) => ({ txnId }),
      transformResponse: (rawResponse: unknown): PaymentContextVerifyModel =>
        adaptPaymentContextVerifyDtoToModel(
          rawResponse as PaymentMaybePayloadDTO<PaymentContextVerifyDTO>
        ),
      keepUnusedDataFor: 0,
    },
    getPaymentContextSuccess: {
      url: '/payment-context/success',
      method: 'GET',
      params: (params: unknown) => {
        const { paymentSource, txnId } = params as PaymentContextResultParams;
        return { paymentSource, txnId };
      },
      transformResponse: (rawResponse: unknown): PaymentContextSuccessModel =>
        adaptPaymentContextSuccessDtoToModel(
          rawResponse as PaymentMaybePayloadDTO<PaymentContextSuccessDTO>
        ),
      keepUnusedDataFor: 0,
    },
    getPaymentContextFailure: {
      url: '/payment-context/failure',
      method: 'GET',
      params: (params: unknown) => {
        const { paymentSource, txnId } = params as PaymentContextResultParams;
        return { paymentSource, txnId };
      },
      transformResponse: (rawResponse: unknown): PaymentContextFailureModel =>
        adaptPaymentContextFailureDtoToModel(
          rawResponse as PaymentMaybePayloadDTO<PaymentContextFailureDTO>
        ),
      keepUnusedDataFor: 0,
    },
  },
});

export const paymentApi = paymentApiSlice.queryClient;
export const paymentApiRedux = paymentApiSlice.queryClient as ReduxQueryClient;

const hooks = paymentApiSlice.hooks as {
  useGetPayViaLinkDetailsQuery: QueryHook<string, PayViaLinkDetailsModel>;
  useGetPaymentContextSuccessQuery: QueryHook<PaymentContextResultParams, PaymentContextSuccessModel>;
  useGetPaymentContextFailureQuery: QueryHook<PaymentContextResultParams, PaymentContextFailureModel>;
};

export const useGetPayViaLinkDetailsQuery = hooks.useGetPayViaLinkDetailsQuery;
export const useGetPaymentContextSuccessQuery = hooks.useGetPaymentContextSuccessQuery;
export const useGetPaymentContextFailureQuery = hooks.useGetPaymentContextFailureQuery;

export class PaymentAdapter implements PaymentRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  private executeEndpoint<TData, TArgs>(
    endpointName: string,
    args: TArgs,
    options?: { forceRefetch?: boolean }
  ): Promise<TData> {
    const endpoint = paymentApi.endpoints[endpointName];

    if (!endpoint || typeof endpoint.initiate !== 'function') {
      throw new Error(`Payment endpoint "${endpointName}" is not registered.`);
    }

    const initiateResult = (endpoint.initiate as EndpointInitiate<TArgs>)(args, options);
    return executeQuery<TData>(initiateResult, this.dispatch);
  }

  async getPayViaLinkDetails(queryString: string): Promise<PayViaLinkDetailsModel> {
    return this.executeEndpoint<PayViaLinkDetailsModel, string>(
      'getPayViaLinkDetails',
      queryString
    );
  }

  async createPaymentRequest(txnId: string): Promise<PaymentRequestModel> {
    return this.executeEndpoint<PaymentRequestModel, string>(
      'createPaymentRequest',
      txnId,
      { forceRefetch: true }
    );
  }

  async verifyPaymentContext(txnId: string): Promise<PaymentContextVerifyModel> {
    return this.executeEndpoint<PaymentContextVerifyModel, string>(
      'verifyPaymentContext',
      txnId,
      { forceRefetch: true }
    );
  }

  async getPaymentContextSuccess(params: PaymentContextResultParams): Promise<PaymentContextSuccessModel> {
    return this.executeEndpoint<PaymentContextSuccessModel, PaymentContextResultParams>(
      'getPaymentContextSuccess',
      params,
      { forceRefetch: true }
    );
  }

  async getPaymentContextFailure(params: PaymentContextResultParams): Promise<PaymentContextFailureModel> {
    return this.executeEndpoint<PaymentContextFailureModel, PaymentContextResultParams>(
      'getPaymentContextFailure',
      params,
      { forceRefetch: true }
    );
  }
}
